import { Component, ChangeDetectionStrategy, signal, inject, computed, viewChild, ElementRef, ChangeDetectorRef, Signal, } from "@angular/core";
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValueChangeEvent } from "@angular/forms";
import { CommonModule, DatePipe, NgFor } from "@angular/common";
import { SettingsService } from "../../services/settings.service";
import { MemberLevel, FlatRate, DaySchedule, } from "../../types/store-settings";
import { ActivatedRoute, RouterLink } from "@angular/router";
import { MasterService } from "@/src/services/master.service";
import { AuthenticationService } from "@/src/services/authentication.service";
import { UsersService } from "@/src/services/user.service";
import { KakaoMapService } from "@/src/services/kakaomap.service";
import { NaverMapService } from "@/src/services/naverMap.service";
import { LanguageService } from "@/src/services/language.service";
import { formatDateToYYYYMMDD, formatDateFromYYYYMMDD } from "@/src/helpers/date-format.helper";
import { Constants } from "@/src/helpers/constants";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { SelectModule } from "primeng/select";
import { DialogModule } from 'primeng/dialog';
import { M_STORE } from "@/src/models/M_STORE";
import { M_MEMBER_LEVEL } from "@/src/models/M_MEMBER_LEVEL";
import { M_MEMBER_PRICE } from "@/src/models/M_MEMBER_PRICE";
import { M_STORE_TIME } from "@/src/models/M_STORE_TIME";
import { M_STORE_HOLIDAY } from "@/src/models/M_STORE_HOLIDAY";
import { M_STORE_USER } from "@/src/models/M_STORE_USER";
import { M_STORE_USER_TIME } from "@/src/models/M_STORE_USER_TIME";
import { InputTextModule } from "primeng/inputtext";
import { TooltipModule } from 'primeng/tooltip';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { InputNumberModule } from 'primeng/inputnumber';
import { DatePickerModule } from 'primeng/datepicker';
import { RadioButtonModule } from 'primeng/radiobutton';
import { TableModule } from 'primeng/table';
import { EnumOption } from "@/src/models/EnumOption";
import { PopUpCommonComponent } from '@/src/components/pop-up-common/pop-up-common.component'
import { MaterialTableComponent } from '../shared/material-table/material-table.component';
import { SortableHeader } from '../../types/table';
import { LanguageService2 } from "@/src/services/language.service2";
import { PasswordModule } from "primeng/password";
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { TextareaModule } from 'primeng/textarea';
import { PopoverModule } from 'primeng/popover';
import { WebSocketService } from "@/src/services/webSocket.service";


const DAYS_ORDER = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

interface CalendarDay {
  day: number;
  date: Date;
  isCurrentMonth: boolean;
  isClosed: boolean;
  isWeeklyClosed: boolean;
  isToday: boolean;
  isHolidayYN: string
}

type SettingsTab = "ENV" | "STAFF" | "MEMBERS" | "HOURS";

@Component({
  selector: "app-settings",
  standalone: true,
  imports: [FormsModule, DatePipe, NgFor, RouterLink, TranslateModule, SelectModule, DialogModule, CommonModule, InputTextModule, TooltipModule, PasswordModule,
    ReactiveFormsModule, ToggleSwitchModule, InputNumberModule, DatePickerModule, RadioButtonModule, TableModule, PopUpCommonComponent,
    MaterialTableComponent, IconFieldModule, InputIconModule, TextareaModule, PopoverModule],
  templateUrl: "./settings.component.html",
  styleUrls: ["./settings.component.css"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SettingsComponent {
  private settingsService = inject(SettingsService);
  private route = inject(ActivatedRoute);
  private masterService = inject(MasterService);
  private auth = inject(AuthenticationService);
  private languageService = inject(LanguageService);
  private fb = inject(FormBuilder);
  private cdr = inject(ChangeDetectorRef)
  private userService = inject(UsersService)
  form!: FormGroup;
  formUser!: FormGroup;

  curentId: string = '';

  locale: any;
  // Component State
  activeTab = signal<SettingsTab>('ENV');
  isLoading = signal(false);

  // File Upload State
  fileInput = viewChild<ElementRef<HTMLInputElement>>("fileInput");
  uploadTarget = signal<{ type: "rep" | "cover"; index?: number } | null>(null);

  P_PAGEROW = Constants.P_PAGEROW;


  // ENV Tab State
  storeInfo = new M_STORE();
  isEditing = signal(false);
  repImageFile = signal<File | null>(null);
  repImageUrl = signal<string | null>(null)

  coverImageFiles = signal<File[]>([]);
  coverImageUrls = signal<string[]>([])

  // STAFF Tab State
  staffList = signal<M_STORE_USER[]>([]);
  selectedStaff = signal<M_STORE_USER>(new M_STORE_USER());
  originalWorkHours = signal<any>(null); // Track original work hours for comparison
  modifiedDays = signal<Set<string>>(new Set()); // Track which days have been modified
  showPassword = signal(false);
  commonEMP_TP = signal<any[]>([]); // HR02 - User Type
  commonEMP_AUTH = signal<any[]>([]); // HR03 - Permission/Role
  commonRETIRE_FG = signal<any[]>([]); // HR01 - Employment Status

  memberLevels = signal<MemberLevel[]>([]);
  flatRates = signal<FlatRate[]>([]);
  memberConfig = signal({
    accDiscType: "Accumulation",
  });

  hoursTab = signal<"SETTINGS" | "SCHEDULE">("SETTINGS");
  // weeklySchedule = signal<DaySchedule[]>([]);
  currentYear = new Date().getFullYear();
  today = new Date();
  isPastYear = computed(() => {
    return this.yearSettingsDate().getFullYear() < this.currentYear;
  });
  currentDate = signal(new Date());
  private normalize(date: Date): number {
    return new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    ).getTime();
  }

  isPastDate = (date: Date) => {
    return this.normalize(date) <= this.normalize(this.today);
  };
  isToday(date: Date): boolean {
    const today = new Date();
    return (
      date.getFullYear() === today.getFullYear() &&
      date.getMonth() === today.getMonth() &&
      date.getDate() === today.getDate()
    );
  }
  yearSettingsDate = signal(new Date());
  yearSettingsDatePicker = new Date();
  closedDates = signal<string[]>([]);//"2025-12-07", "2025-12-25"
  weeklyClosedDays = signal<string[]>([]); // Sunday
  paramStaff = {
    P_HQ_CD: "",
    P_STR_CD: "",
    P_TEL_NO: "",
    P_USER_NM: "",
  };
  paramStaffWorkTime = {
    P_HQ_CD: "",
    P_STR_CD: "",
    P_USER_ID: "",

  };

  constructor(private kakaoMapService: KakaoMapService, private naverMap: NaverMapService, private translate: TranslateService,
    private language: LanguageService2, private webSocket: WebSocketService) {
    // this.weeklySchedule.set(this.settingsService.getWeeklySchedule()());
    this.locale = language.getLocale();
  }
  async ngOnInit() {
    this.curentId = this.auth.getUserId();
    this.getImgInfo();
    this.route.paramMap.subscribe(params => {
      const tab = params.get('tab')?.toUpperCase() || 'ENV';
      this.activeTab.set(tab as SettingsTab);
    });
    this.paramStaff.P_HQ_CD = this.auth.getHQCD();
    this.paramStaff.P_STR_CD = this.auth.getStrCd();
    this.paramStaffWorkTime.P_HQ_CD = this.auth.getHQCD();
    this.paramStaffWorkTime.P_STR_CD = this.auth.getStrCd();
    this.getDataCommonUserType();
    this.getDataCommonPermission();
    this.getDataCommonEmploymentStatus();
    this.getStaffList();

    //store
    this.getCommonStoreTab();
    this.getStoreInfo();


    // member
    await this.getDayCommon();

    this.getCommonMemberTab();
    this.searchMemberShip();

    //hours
    this.getDataBusinessHours();
  }
  getDataCommonUserType() {
    this.masterService.getComm("HR02").subscribe((res: any) => {
      const dataRes = res?.result?.dataRes[0].rows ?? [];
      this.commonEMP_TP.set(dataRes);
    });
  }

  getDataCommonPermission() {
    this.masterService.getComm("HR03").subscribe((res: any) => {
      const dataRes = res?.result?.dataRes[0].rows ?? [];
      this.commonEMP_AUTH.set(dataRes);
    });
  }

  getDataCommonEmploymentStatus() {
    this.masterService.getComm("HR01").subscribe((res: any) => {
      const dataRes = res?.result?.dataRes[0].rows ?? [];
      this.commonRETIRE_FG.set(dataRes);
    });
  }

  daySchedulesMap = computed(() => {
    const staff = this.selectedStaff();
    if (!staff?.workHours) return new Map<string, any[]>();

    const map = new Map<string, any[]>();
    for (const item of staff.workHours) {
      if (!map.has(item.day)) {
        map.set(item.day, []);
      }
      map.get(item.day)!.push(item);
    }
    return map;
  })
  getUserWorkTime(userId: string) {
    const params = {
      ...this.paramStaffWorkTime,
      P_USER_ID: userId
    };
    //SP_STR_GET_USER_WORK_TIME
    this.masterService
      .excute_sp("6a83a0d8-298d-11f1-9014-00155d001e02", params)
      .subscribe((res: any) => {
        let dataRes = new M_STORE_USER_TIME().getAllSchedules(res?.result?.dataRes[0].rows ?? []);
        const existingDays = new Set(
          dataRes.map(x => x.day)
        );

        const newItems = this.dayCommons
          .filter(dc => !existingDays.has(dc.comM_D_CD))
          .map(dc => {
            const item = new M_STORE_USER_TIME();
            item.useR_ID = this.selectedStaff()?.useR_ID;
            item.day = dc.comM_D_CD;
            item.starT_TIME = new M_STORE_TIME().formatTime('')
            item.enD_TIME = new M_STORE_TIME().formatTime('')
            item.daY_SEQ = 0;
            return item;
          });
        dataRes = [...dataRes, ...newItems];
        dataRes.sort((a, b) =>
          Number(a.day) - Number(b.day)
        );
        this.selectedStaff.update(current => {
          return new M_STORE_USER({
            ...current,
            workHours: [...dataRes]
          });
        });
      });
  }

  totalCnt = signal(0);
  isNextPage = signal(false)

  nextPage(page: number) {
    this.getStaffList(page, true);
  }
  getStaffList(P_PAGEINDEX: number = 1, isNextPage: boolean = false) {
    this.formUser = new M_STORE_USER().createForm(this.fb);
    this.isNextPage.set(isNextPage)
    const param = {
      ...this.paramStaff,
      P_TEL_NO: this.paramStaff.P_TEL_NO.replaceAll("-", ""),
      P_PAGEINDEX: P_PAGEINDEX,
      P_PAGEROW: this.P_PAGEROW
    }
    //SP_STR_GET_USER_LIST
    this.masterService
      .excute_sp("6a83a011-298d-11f1-9014-00155d001e03", param)
      .subscribe((res: any) => {
        if (res?.isOK) {
          this.totalCnt.set(res?.result?.dataRes[0]?.rows[0]?.totalcnt)
          const newData = res?.result?.dataRes[1]?.rows || [];
          this.selectedStaff.set(new M_STORE_USER())
          this.staffList.set(new M_STORE_USER().getAllEmployees(newData))
          this.cdr.detectChanges();
        }
        this.formUser.get('emP_TP')?.patchValue(this.commonEMP_TP()[0]?.comM_D_CD);
        this.formUser.get('retirE_FG')?.patchValue(this.commonRETIRE_FG()[0]?.comM_D_CD);
        this.formUser.get('emP_AUTH')?.patchValue(this.commonEMP_AUTH()[0]?.comM_D_CD);
      });
  }

  // tab store setting
  showActiveKey = signal(false);
  activeKey = '';
  getActiveKey() {
    const data = `${this.storeInfo.hQ_CD}${this.storeInfo.stR_CD}`
    this.activeKey = this.settingsService.encode(data);
    this.showActiveKey.set(true);
  }

  loading = signal(false);
  loadingText = signal('');
  setLoading(text: string) {
    this.loading.set(true);
    this.loadingText.set(text);
  }

  clearLoading() {
    this.loading.set(false);
    this.loadingText.set('');
  }

  updateAgent() {
    this.settingsService.updateAgent().subscribe(async (res: any) => {
      if (res?.isOK) {
        this.setLoading('');
        const paramWs = res?.result?.dataRes;
        try {
          await this.webSocket.waitForOpen();
          const ws = await this.webSocket.sendMessage(paramWs);

          if (ws.type) {
            this.masterService.showSuccess(
              this.translate.instant('AGENTSTR0003')
            );
          }

        } catch (err) {
          this.masterService.showError(
              this.translate.instant('AGENTSTR0001')
            );
        }
        this.clearLoading();
      }
      else {
        this.masterService.showError(Constants.showError(res?.errorMessages))
      }
    })
  }
  onTimePickerOpen(name: string) {
    const control = this.form.get(name);

    if (!control?.value) {
      control?.setValue(this.roundTo15(new Date()));
    }
  }

  formatTime(controlName: string) {
    const control = this.form.get(controlName);
    if (!control?.value) return;

    // 👉 round
    const rounded = this.roundTo15(control.value);
    control.setValue(rounded, { emitEvent: false });

    const startCtrl = this.form.get('reservE_START_TIME');
    const endCtrl = this.form.get('reservE_END_TIME');

    const start = startCtrl?.value;
    const end = endCtrl?.value;

    // 👉 giống schedule: chỉ fix start
    if (start && end) {
      if (start >= end) {
        const newStart = new Date(end.getTime() - 15 * 60 * 1000);
        startCtrl.setValue(newStart, { emitEvent: false });
      }
    }
  }

  onTimeInput(event: any, formName: string) {
    let value = event.target.value.replace(/\D/g, '');

    if (!value) {
      this.form.get(formName)?.setValue(null, { emitEvent: false });
      return;
    }

    if (value.length > 4) value = value.substring(0, 4);

    if (value.length < 4) {
      event.target.value = value;
      return;
    }

    let hour = parseInt(value.substring(0, 2), 10);
    let minute = parseInt(value.substring(2, 4), 10);

    if (hour > 23) hour = 0;
    if (minute > 59) minute = 59;

    const now = new Date();
    let date = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      hour,
      minute,
      0
    );

    const startCtrl = this.form.get('reservE_START_TIME');
    const endCtrl = this.form.get('reservE_END_TIME');

    if (formName === 'reservE_START_TIME') {

      const end = endCtrl?.value;

      if (end && date >= end) {
        date = new Date(end.getTime() - 15 * 60 * 1000);
      }

      startCtrl?.setValue(date, { emitEvent: false });

    } else {

      endCtrl?.setValue(date, { emitEvent: false });

      const start = startCtrl?.value;
      const end = endCtrl?.value;

      if (start && start >= end) {
        const newStart = new Date(end.getTime() - 15 * 60 * 1000);
        startCtrl?.setValue(newStart, { emitEvent: false });
      }
    }

    const finalDate =
      formName === 'reservE_START_TIME'
        ? startCtrl?.value
        : endCtrl?.value;

    const h = finalDate.getHours().toString().padStart(2, '0');
    const m = finalDate.getMinutes().toString().padStart(2, '0');

    event.target.value = `${h}:${m}`;
  }
  roundTo15(date: Date): Date {
    const minutes = date.getMinutes();
    const rounded = Math.ceil(minutes / 15) * 15;

    const d = new Date(date);
    d.setSeconds(0);
    d.setMilliseconds(0);

    if (rounded === 60) {
      d.setHours(d.getHours() + 1);
      d.setMinutes(0);
    } else {
      d.setMinutes(rounded);
    }

    return d;
  }

  getCommonStoreTab() {
    this.getBizType();
    this.getComport();
    this.getBaudrate();
    this.getCardDev();
    this.getTimeFG();
  }
  visible: boolean = false;
  getStoreInfo() {
    this.form = new M_STORE().createForm(this.fb);
    const param = {
      P_HQ_CD: this.auth.getHQCD(),
      P_STR_CD: this.auth.getStrCd()
    }
    //SP_STR_GET_INFO
    this.masterService.excute_sp('6a839b98-298d-11f1-9014-00155d001e02', param).subscribe((res: any) => {
      if (res?.isOK) {
        const data = new M_STORE().getStore(res?.result?.dataRes[0]?.rows[0])
        this.storeInfo = data;
        this.oldmembeR_FG = this.storeInfo.membeR_FG
        this.oldsavE_CHARGE_FG = this.storeInfo.savE_CHARGE_FG
        data.reserveChecked = data.reservE_YN === "Y";
        this.form.patchValue(data);
        this.disableEdit();
        const startCtrl = this.form.get('reservE_START_TIME');
        const endCtrl = this.form.get('reservE_END_TIME');

        startCtrl?.valueChanges.subscribe(start => {
          const end = endCtrl?.value;
          if (!start || !end) return;

          const startMinutes = start.getHours() * 60 + start.getMinutes();
          const endMinutes = end.getHours() * 60 + end.getMinutes();

          if (endMinutes <= startMinutes) {
            endCtrl?.setValue(new Date(start), { emitEvent: false });
          }
        });

        endCtrl?.valueChanges.subscribe(end => {
          const start = startCtrl?.value;
          if (!start || !end) return;

          const startMinutes = start.getHours() * 60 + start.getMinutes();
          const endMinutes = end.getHours() * 60 + end.getMinutes();

          if (endMinutes <= startMinutes) {
            endCtrl?.setValue(new Date(start), { emitEvent: false });
          }
        });
      }
    })
  }

  getImgInfo() {
    const param = {
      P_HQ_CD: this.auth.getHQCD(),
      P_STR_CD: this.auth.getStrCd()
    }
    //SP_STR_GET_INFO_IMAGE
    this.masterService.excute_sp('6a839bcb-298d-11f1-9014-00155d001e02', param).subscribe(async (res: any) => {
      if (res?.isOK) {
        await this.parseImagesFromDb(res?.result?.dataRes[0]?.rows);
      }
    })
  }

  defaultImage = 'assets/icons/my-account.png';

  onImageError(event: Event) {
    const img = event.target as HTMLImageElement;
    img.src = this.defaultImage;
  }

  async parseImagesFromDb(images: any[]) {
    if (!Array.isArray(images)) return;

    /* ========= REP ========= */
    const rep = images.find(x => x.imagE_FG === '0');
    if (rep?.imagE_PATH) {
      const repFile = await this.urlToFile(rep.imagE_PATH);

      this.repImageFile.set(repFile);
      this.repImageUrl.set(rep.imagE_PATH);
    } else {
      this.repImageFile.set(null);
      this.repImageUrl.set(null);
    }

    /* ========= COVER ========= */
    const coverItems = images
      .filter(x => x.imagE_FG === '1')
      .sort((a, b) => a.imagE_SEQ - b.imagE_SEQ);

    const coverFiles: File[] = [];
    const coverUrls: string[] = [];

    for (const c of coverItems) {
      if (!c.imagE_PATH) continue;

      const file = await this.urlToFile(c.imagE_PATH);
      coverFiles.push(file);
      coverUrls.push(c.imagE_PATH);
    }

    this.coverImageFiles.set(coverFiles);
    this.coverImageUrls.set(coverUrls);
  }

  async urlToFile(url: string, filename?: string): Promise<File> {
    const res = await fetch(url);
    const blob = await res.blob();

    const name =
      filename ??
      url.split('/').pop() ??
      `image_${Date.now()}.jpg`;

    return new File([blob], name, { type: blob.type });
  }

  triggerFileUpload(type: "rep" | "cover", index?: number) {
    this.uploadTarget.set({ type, index });
    setTimeout(() => {
      const input = this.fileInput()?.nativeElement;
      if (!input) return;

      input.value = '';
      input.click();
    });
  }

  private isImage(file: File): boolean {
    return file.type.startsWith('image/');
  }
  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const target = this.uploadTarget();
    if (!target) return;

    /* ========= REPRESENTATIVE (ONLY 1 FILE) ========= */
    if (target.type === 'rep') {
      const file = input.files[0];
      if (!this.isImage(file)) {
        this.masterService.showError(
          this.translate.instant('SETNGSTR0026')
        );
        input.value = '';
        return;
      }

      this.revokeUrl(this.repImageUrl());

      this.repImageFile.set(file);
      this.repImageUrl.set(URL.createObjectURL(file));
      this.handleRepSave();
    }

    /* ========= COVER (MULTI FILE) ========= */
    else if (target.type === 'cover') {
      const files = Array.from(input.files);
      const imageFiles = files.filter(f => this.isImage(f));
      if (imageFiles.length !== files.length) {
        this.masterService.showError(
          this.translate.instant('SETNGSTR0026')
        );
        input.value = '';
        return;
      }

      if (this.coverImageFiles().length + files.length > 10) {
        let message = this.translate.instant('NOTIFIHQ0032', { n: 10 })
        this.masterService.showError(message);
      }
      const remain = 10 - this.coverImageFiles().length;
      const filesToAdd = files.slice(0, remain);

      this.coverImageFiles.update(list => [...list, ...filesToAdd]);
      this.coverImageUrls.update(list => [
        ...list,
        ...filesToAdd.map(f => URL.createObjectURL(f))
      ]);
    }

    input.value = '';
    this.uploadTarget.set(null);
  }
  private revokeUrl(url: string | null | undefined) {
    if (url) {
      URL.revokeObjectURL(url);
    }
  }
  handleStoreSave() {
    if (this.isEditMode) {
      if (this.form.valid) {
        const payload = new M_STORE(this.form.getRawValue());
        payload.reservE_YN = payload.reserveChecked ? 'Y' : 'N';
        this.settingsService.post(payload.toJSON()).subscribe((res: any) => {
          if (res?.isOK) {
            this.masterService.showSuccess(this.translate.instant('SYSMESER0006'))
            this.disableEdit();
          }
          else {
            this.masterService.showError(Constants.showError(res?.errorMessages))
          }
        })
      }
      else {
        this.form.markAllAsTouched();
      }
    }
    else {
      this.enableEdit();
    }
  }

  handleCoverSave() {
    this.settingsService.postCovers(this.coverImageFiles()).subscribe((res: any) => {
      if (res?.isOK) {
        const message = this.coverImageFiles().length > 0 ? 'SETNGSTR0031' : 'SETNGSTR0032'
        this.masterService.showSuccess(this.translate.instant(message))
      }
      else {
        this.masterService.showError(Constants.showError(res?.errorMessages))
      }
    })
  }

  handleRepSave() {
    const file = this.repImageFile();

    if (!file) return;

    this.settingsService.postRep(file).subscribe((res: any) => {
      if (res?.isOK) {
        this.masterService.showSuccess(this.translate.instant('SETNGSTR0030'));
      } else {
        this.masterService.showError(Constants.showError(res?.errorMessages));
      }
    });
  }
  removeCoverImage(index: number, all: boolean = false) {
    if (all) {
      this.coverImageFiles.set([]);
      this.coverImageUrls.set([]);
      return;
    }
    this.coverImageFiles.update(files => {
      const newFiles = [...files];
      newFiles.splice(index, 1);
      return newFiles;
    });

    this.coverImageUrls.update(urls => {
      const newUrls = [...urls];
      this.revokeUrl(newUrls[index]);
      newUrls.splice(index, 1);
      return newUrls;
    });
  }
  bizTypeList = new Array();
  getBizType() {
    this.masterService.getCommNoLoading('ST01').then((res: any) => {
      if (res?.isOK) {
        this.bizTypeList = res?.result?.dataRes[0]?.rows;
        this.cdr.detectChanges();
      }
    })
  }

  comportList = [];

  getComport() {
    this.masterService.getCommNoLoading('ST23').then((res: any) => {
      if (res?.isOK) {
        this.comportList = res?.result?.dataRes[0]?.rows;
        this.cdr.detectChanges();
      }
    })
  }
  baudrateList = [];
  getBaudrate() {
    this.masterService.getCommNoLoading('ST24').then((res: any) => {
      if (res?.isOK) {
        this.baudrateList = res?.result?.dataRes[0]?.rows;
        this.cdr.detectChanges();
      }
    })
  }
  cardDevList = []
  getCardDev() {
    this.masterService.getCommNoLoading('ST30').then((res: any) => {
      if (res?.isOK) {
        this.cardDevList = res?.result?.dataRes[0]?.rows;
        this.cdr.detectChanges();
      }
    })
  }

  timeFGList = []
  getTimeFG() {
    this.masterService.getCommNoLoading('ST20').then((res: any) => {
      if (res?.isOK) {
        this.timeFGList = res?.result?.dataRes[0]?.rows;
        this.cdr.detectChanges();
      }
    })
  }

  openPostcodeSearch(isStore: boolean = true) {
    this.visible = true;

    setTimeout(() => {
      this.kakaoMapService.loadKakaoMapScript()
        .then(() => {
          return this.kakaoMapService.loadPostcodeScript();
        })
        .then(() => {
          const wrapper = document.getElementById('postcode-wrapper');
          if (!wrapper) return;

          wrapper.innerHTML = '';

          let element = document.createElement('div');
          element.id = 'postcode-container';

          const contentDiv = document.createElement('div');
          contentDiv.className = 'postcode-content';
          contentDiv.id = 'postcode-content';
          contentDiv.style.height = '80vh';

          element.appendChild(contentDiv);
          wrapper.appendChild(element);

          const postcode = new (window as any).daum.Postcode({
            oncomplete: (data: any) => {
              let address = data.address;
              if (isStore) this.form.get('addr')?.patchValue(address);
              else this.formUser.get('addr')?.patchValue(address);
              this.naverMap.getLatLng(address).subscribe({
                next: (coords: any) => {
                  if (coords.result.dataRes) {
                    // this.storeDto.lat = coords.result.dataRes.addresses[0].y;
                    // this.storeDto.long = coords.result.dataRes.addresses[0].x;
                    // this.userForm.get('lat')?.setValue(coords.result.dataRes.addresses[0].y);
                    // this.userForm.get('long')?.setValue(coords.result.dataRes.addresses[0].x);
                  }

                  this.visible = false;
                  wrapper.innerHTML = '';
                  this.cdr.detectChanges()
                },
                error: (error: any) => {
                  console.error('Lỗi lấy tọa độ:', error);
                }
              });
            },
            width: '100%',
            height: '100%'
          });

          postcode.embed(contentDiv);
        })
        .catch((error) => {
          this.visible = false;
          this.cdr.detectChanges()
          console.error('Lỗi tải Postcode API hoặc Kakao Maps:', error);
        });
    }, 100);
  }

  isEditMode = false;

  enableEdit() {
    this.isEditMode = true;
    const editableControls = new M_STORE().editableControls;
    editableControls.forEach(name => {
      this.form.get(name)?.enable();
    });
  }

  disableEdit() {
    this.isEditMode = false;
    const editableControls = new M_STORE().editableControls;
    editableControls.forEach(name => {
      this.form.get(name)?.disable();
    });
  }

  // end store setting

  staffHeaders = computed<SortableHeader[]>(() => {
    return [
      { key: 'name', label: this.translate.instant('STAFFSTR0008'), sortable: true, width: 120 },
      { key: 'userId', label: this.translate.instant('STAFFSTR0009'), sortable: true, width: 120 },
      { key: 'phone', label: this.translate.instant('STAFFSTR0010'), sortable: true, width: 120 },
    ];
  });

  resetStaffSearch() {
    this.paramStaff.P_TEL_NO = "";
    this.paramStaff.P_USER_NM = "";
    this.getStaffList();
  }

  selectStaff(staff: M_STORE_USER) {
    this.selectedStaff.set(staff);
    this.formUser.patchValue(staff);
    this.isUserId = false;
    this.isTelNo = false;
    this.formUser.get('useR_ID')?.disable();
    this.originalWorkHours.set(null);
    if (staff.useR_ID) {
      this.getUserWorkTime(staff.useR_ID);
    }
  }

  onCLickNewStaff() {
    this.selectedStaff.set(new M_STORE_USER())
    this.formUser = new M_STORE_USER().createForm(this.fb);
    this.isUserId = false;
    this.isTelNo = false;
    this.formUser.get('retirE_FG')?.patchValue(this.commonRETIRE_FG()[0]?.comM_D_CD);
    this.formUser.get('emP_TP')?.patchValue(this.commonEMP_TP()[0]?.comM_D_CD);
    this.formUser.get('emP_AUTH')?.patchValue(this.commonEMP_AUTH()[0]?.comM_D_CD);
  }

  addWorkHourSequence(day: string) {
    const workHour = this.selectedStaff().workHours?.filter(x => x.day === day);
    if (workHour.length > 1) return;
    this.selectedStaff.update(current => {
      if (!current) return current;
      const maxSeq = workHour.length
        ? Math.max(...workHour.map(x => x.daY_SEQ ?? 0))
        : 0;
      let newTime = new M_STORE_USER_TIME();
      newTime.day = day;
      newTime.daY_SEQ = maxSeq + 1;
      newTime.useR_ID = this.selectedStaff()?.useR_ID;

      return new M_STORE_USER({
        ...current,
        workHours: [...current.workHours, newTime]
      });
    });
  }

  removeWorkHourSequence(day: string, seq: number) {
    this.selectedStaff.update(current => {
      if (!current) return current;

      const updatedWorkHours = current.workHours
        ?.filter(x => !(x.day === day && x.daY_SEQ === seq)) ?? [];

      return new M_STORE_USER({
        ...current,
        workHours: updatedWorkHours
      });
    });

  }

  submitted: boolean = false;
  saveStaff() {
    this.submitted = true;
    // console.log(this.formUser.valid)
    if (this.formUser.valid) {
      const payload = new M_STORE_USER(this.formUser.getRawValue());
      this.settingsService.postStaff(payload).subscribe((res: any) => {
        if (res?.isOK) {
          if (payload.hQ_CD == "") this.masterService.showSuccess(this.translate.instant("USERMAG0045"));
          else this.masterService.showSuccess(this.translate.instant("SYSMESER0006"));
          this.isTelNo = false;
          this.isUserId = false;
          this.getStaffList();
        }
        else {
          this.masterService.showError(Constants.showError(res?.errorMessages))
        }
        this.submitted = false;
      })
    }
    else {
      this.formUser.markAllAsTouched();
    }
  }

  onClickshowConfirmDeleteStaff() {
    this.showConfirmDeleteStaff.set(true);
  }

  deleteStaff() {
    const payload = new M_STORE_USER(this.formUser.getRawValue());
    this.settingsService.deleteStaff(payload).subscribe((res: any) => {
      if (res?.isOK) {
        this.getStaffList();
        this.masterService.showSuccess(this.translate.instant("SYSMESER0007"));
      }
      else {
        this.masterService.showError(Constants.showError(res?.errorMessages))
      }
      this.showConfirmDeleteStaff.set(false);
    })
  }

  saveStaffTime() {
    const payload = this.selectedStaff().workHours.map(x => x.toJSON());
    this.settingsService.postStaffTime(payload).subscribe((res: any) => {
      if (res?.isOK) this.masterService.showSuccess(this.translate.instant("SYSMESER0006"));
      else this.masterService.showError(Constants.showError(res?.errorMessages));
    })
  }
  changeDate() {
    const openCtrl = this.formUser.get('hR_DT');
    const closeCtrl = this.formUser.get('retirE_DT');

    openCtrl?.valueChanges.subscribe(open => {
      const close = closeCtrl?.value;
      if (open && close && new Date(close) < new Date(open)) {
        closeCtrl?.setValue(open, { emitEvent: false });
      }
    });

    closeCtrl?.valueChanges.subscribe(close => {
      const open = openCtrl?.value;
      if (open && close && new Date(close) < new Date(open)) {
        openCtrl?.setValue(close, { emitEvent: false });
      }
    });

    const open = openCtrl?.value;
    const close = closeCtrl?.value;
    if (open && close && new Date(close) < new Date(open)) {
      openCtrl?.setValue(close, { emitEvent: false });
    }
  }

  onPhoneInput(event: Event, field: string) {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replace(/[^0-9]/g, '');

    if (input.value.length <= 10) {
      input.value = input.value.replace(/(\d{3})(\d{3})(\d{0,4})/, (_, a, b, c) => {
        return c ? `${a}-${b}-${c}` : `${a}-${b}`;
      });
    } else {
      input.value = input.value.substring(0, 11);
      input.value = input.value.replace(/(\d{3})(\d{4})(\d{0,4})/, (_, a, b, c) => {
        return c ? `${a}-${b}-${c}` : `${a}-${b}`;
      });
    }

    this.formUser.get(field)?.setValue(input.value, { emitEvent: false });
  }

  onPhoneInputSearch(event: Event) {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replace(/[^0-9]/g, '');

    if (input.value.length <= 10) {
      input.value = input.value.replace(/(\d{3})(\d{3})(\d{0,4})/, (_, a, b, c) => {
        return c ? `${a}-${b}-${c}` : `${a}-${b}`;
      });
    } else {
      input.value = input.value.substring(0, 11);
      input.value = input.value.replace(/(\d{3})(\d{4})(\d{0,4})/, (_, a, b, c) => {
        return c ? `${a}-${b}-${c}` : `${a}-${b}`;
      });
    }

    this.paramStaff.P_TEL_NO = input.value;
  }

  togglePasswordVisibility() {
    this.showPassword.update((v) => !v);
  }

  isTelNo: boolean = false;
  checkTelNo() {
    const telNo = this.formUser.get('teL_NO')?.value ? this.formUser.get('teL_NO')?.value : undefined
    const userId = this.formUser.get('useR_ID')?.value && this.formUser.get('hQ_CD')?.value ? this.formUser.get('useR_ID')?.value : undefined
    this.userService.checkTelNoExists(telNo, userId).subscribe((res: any) => {
      if (res?.isOK) {
        this.isTelNo = true;
      }
      else {
        this.isTelNo = false;
        this.masterService.showError(Constants.showError(res?.errorMessages))
      }
      this.cdr.detectChanges()
    })
  }

  isUserId: boolean = false;
  checkUserId() {
    this.userService.checkUserNameExists(this.formUser.get('useR_ID')?.value ? this.formUser.get('useR_ID')?.value : null).subscribe((res: any) => {
      if (res?.isOK) {
        this.isUserId = true;
      }
      else {
        this.isUserId = false;
        this.masterService.showError(Constants.showError(res?.errorMessages))
      }
      this.cdr.detectChanges()
    }
    )
  }

  onTimePickerStaffOpen(schedule: any, isStart: boolean = true) {
    if (isStart) {
      if (!schedule.starT_TIME) {
        schedule.starT_TIME = this.roundTo15(new Date());
      }
    } else {
      if (!schedule.enD_TIME) {
        schedule.enD_TIME = this.roundTo15(new Date());
      }
    }
  }

  formatTimeStaffOpen(schedule: any, isStart: boolean = true) {

    if (isStart) {
      if (schedule.starT_TIME) {
        schedule.starT_TIME = this.roundTo15(schedule.starT_TIME);
      }
    } else {
      if (schedule.enD_TIME) {
        schedule.enD_TIME = this.roundTo15(schedule.enD_TIME);
      }
    }

    if (schedule.starT_TIME && schedule.enD_TIME) {

      if (schedule.starT_TIME >= schedule.enD_TIME) {
        schedule.starT_TIME = new Date(
          schedule.enD_TIME.getTime() - 15 * 60 * 1000
        );
      }

      const diff = schedule.enD_TIME.getTime() - schedule.starT_TIME.getTime();

      if (diff < 15 * 60 * 1000) {
        schedule.enD_TIME = new Date(
          schedule.starT_TIME.getTime() + 15 * 60 * 1000
        );
      }
    }
  }

  onTimeInputStaffOpen(event: any, schedule: any, isStart: boolean = true) {
    let value = event.target.value.replace(/\D/g, '');

    if (!value) {
      return;
    }

    if (value.length > 4) {
      value = value.substring(0, 4);
    }

    if (value.length < 4) {
      event.target.value = value;
      return;
    }

    let hour = parseInt(value.substring(0, 2), 10);
    let minute = parseInt(value.substring(2, 4), 10);

    if (hour > 23) hour = 0;
    if (minute > 59) minute = 59;

    const now = new Date();
    let date = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      hour,
      minute,
      0
    );

    if (isStart) {
      if (schedule.enD_TIME && date >= schedule.enD_TIME) {
        date = new Date(schedule.enD_TIME.getTime() - 15 * 60 * 1000);
      }

      schedule.starT_TIME = date;

    } else {
      schedule.enD_TIME = date;

      if (schedule.starT_TIME && schedule.starT_TIME >= schedule.enD_TIME) {
        schedule.starT_TIME = new Date(
          schedule.enD_TIME.getTime() - 15 * 60 * 1000
        );
      }
    }

    const h = date.getHours().toString().padStart(2, '0');
    const m = date.getMinutes().toString().padStart(2, '0');
    event.target.value = `${h}:${m}`;
  }

  // --- MEMBERS Tab Methods ---
  actionAdd = EnumOption.Add;
  actionDelete = EnumOption.Delete;
  deleteType = signal<"LEVEL" | "PRICE">("LEVEL");
  showConfirmDelete = signal<boolean>(false);
  showConfirmDeleteStaff = signal<boolean>(false);
  updateStore() {
    this.settingsService.updateStoreMemberShip(this.storeInfo).subscribe((res: any) => {
      if (res?.isOK) {
        this.masterService.showSuccess(this.translate.instant('SYSMESER0006'))
      }
      else
        this.masterService.showError(Constants.showError(res?.errorMessages))
    })
  }

  updateOrDeleteMemberLvls(option: EnumOption) {
    const data =
      option === EnumOption.Add
        ? (this.selectedLevelEdit ? [this.selectedLevelEdit] : [])
        : (this.selectedLevels ?? []);

    if (data.length === 0) return;
    this.settingsService.memberLevelPost(data, option).subscribe((res: any) => {
      if (res?.isOK) {
        var message = 'SERVCSTR0033';
        if (option == EnumOption.Delete) {
          message = 'SYSMESER0007';
          this.showConfirmDelete.set(false);
        }
        else if (option == EnumOption.Add && !this.selectedLevelEdit.newFG) message = 'SYSMESER0006';
        this.masterService.showSuccess(this.translate.instant(message))
        this.getLevel();
        this.selectedLevels = [];
        this.selectedLevelEdit = null;
      }
      else {
        this.masterService.showError(Constants.showError(res?.errorMessages))
      }
    })
  }

  onClickshowConfirmDelete(deleteType: 'LEVEL' | 'PRICE') {
    this.deleteType.set(deleteType);
    this.showConfirmDelete.set(true);
  }

  deleteLevelOrMember() {
    if (this.deleteType() == 'LEVEL') this.updateOrDeleteMemberLvls(EnumOption.Delete);
    else this.updateOrDeleteMemberPrices(EnumOption.Delete);
  }

  updateOrDeleteMemberPrices(option: EnumOption) {
    const data =
      option === EnumOption.Add
        ? [this.selectedMemberPriceEdit]
        : this.selectedMemberPrices ?? [];

    if (data.length === 0) return;
    this.settingsService.memberPricePost(data, option).subscribe((res: any) => {
      if (res?.isOK) {
        var message = 'SERVCSTR0034';
        if (option == EnumOption.Delete) {
          message = 'SYSMESER0007';
          this.showConfirmDelete.set(false);
        }
        else if (option == EnumOption.Add && !this.selectedMemberPriceEdit.newFG) message = 'SYSMESER0006';
        this.masterService.showSuccess(this.translate.instant(message))
        this.selectedMemberPrices = [];
        this.selectedMemberPriceEdit = null;
        this.getMemberPrice();
      }
      else {
        this.masterService.showError(Constants.showError(res?.errorMessages))
      }
    })
  }
  getCommonMemberTab() {
    this.getMemberTP();
    this.getMemberLvl();
    this.getLvlPeriod();
    this.getMemberFG();
    this.getSaveChargeFG();
  }

  firstSearch: boolean = true;
  searchMemberShip() {
    if (!this.firstSearch) return;
    this.getLevel();
    this.getMemberPrice();
    this.firstSearch = false;
  }

  levels = new Array<M_MEMBER_LEVEL>();
  selectedLevels = [];
  selectedLevelEdit: any;
  getLevel() {
    //SP_STR_GET_MEMBER_LVL
    this.masterService.excute_sp('6a839ca0-298d-11f1-9014-00155d001e02', this.paramStaffWorkTime).subscribe((res: any) => {
      if (res?.isOK) {
        this.levels = new M_MEMBER_LEVEL().getAll(res?.result?.dataRes[0]?.rows);
        this.levels.forEach((e: M_MEMBER_LEVEL, index: number) => {
          e.no = index + 1;
        })
        this.cdr.detectChanges();
      }
    })
  }

  selectEdit(data: any, isLevel: boolean = false) {
    if (isLevel) this.selectedLevelEdit = data;
    else this.selectedMemberPriceEdit = data;
    this.cdr.detectChanges();
  }

  memberPrices = new Array<M_MEMBER_PRICE>();
  selectedMemberPrices = [];
  selectedMemberPriceEdit: any;
  getMemberPrice() {
    //SP_STR_GET_MEMBER_PRICE
    this.masterService.excute_sp('6a839cd3-298d-11f1-9014-00155d001e02', this.paramStaffWorkTime).subscribe((res: any) => {
      if (res?.isOK) {
        this.memberPrices = new M_MEMBER_PRICE().getAll(res?.result?.dataRes[0]?.rows);
        this.memberPrices.forEach((e: M_MEMBER_PRICE, index: number) => {
          e.no = index + 1;
        })
        this.cdr.detectChanges();
      }
    })
  }

  memberTPList = new Array();
  getMemberTP() {
    this.masterService.getCommNoLoading('ST25').then((res: any) => {
      if (res?.isOK) {
        this.memberTPList = res?.result?.dataRes[0]?.rows;
        this.cdr.detectChanges();
      }
    })
  }
  onMemberTypeChange(product: any, value: any) {
    const memberTP = this.memberTPList.find(x => x.comM_D_CD == value);
    product.membeR_TP_NM = memberTP.comM_D_NM;
  }

  memberLvlList = new Array();
  getMemberLvl() {
    this.masterService.getCommNoLoading('ST26').then((res: any) => {
      if (res?.isOK) {
        this.memberLvlList = res?.result?.dataRes[0]?.rows;
        this.cdr.detectChanges();
      }
    })
  }

  onMemberLvlChange(product: any, value: any) {
    const memberLvl = this.memberLvlList.find(x => x.comM_D_CD == value);
    product.membeR_LVL_NM = memberLvl.comM_D_NM;
  }

  LvlPeriodList = new Array();
  getLvlPeriod() {
    this.masterService.getCommNoLoading('ST21').then((res: any) => {
      if (res?.isOK) {
        this.LvlPeriodList = res?.result?.dataRes[0]?.rows;
        this.cdr.detectChanges();
      }
    })
  }

  onLvlPeriodChange(product: any, value: any) {
    const lvL_USE_PERIOD = this.LvlPeriodList.find(x => x.comM_D_CD == value);
    product.lvL_USE_PERIOD_NM = lvL_USE_PERIOD.comM_D_NM;
  }

  MemberFGList = new Array();
  getMemberFG() {
    this.masterService.getCommNoLoading('HR06').then((res: any) => {
      if (res?.isOK) {
        this.MemberFGList = res?.result?.dataRes[0]?.rows;
        this.cdr.detectChanges();
      }
    })
  }

  SaveChargeFGList = new Array();
  getSaveChargeFG() {
    this.masterService.getCommNoLoading('HR07').then((res: any) => {
      if (res?.isOK) {
        this.SaveChargeFGList = res?.result?.dataRes[0]?.rows;
        this.cdr.detectChanges();
      }
    })
  }

  toggleCheckLevel(id: string) {
    this.memberLevels.update((levels) =>
      levels.map((l) => (l.id === id ? { ...l, checked: !l.checked } : l)),
    );
  }

  toggleCheckRate(id: string) {
    this.flatRates.update((rates) =>
      rates.map((r) => (r.id === id ? { ...r, checked: !r.checked } : r)),
    );
  }

  toggleAllLevels(event: Event) {
    const target = event.target as HTMLInputElement;
    const isChecked = target.checked;
    this.memberLevels.update((levels) =>
      levels.map((l) => ({ ...l, checked: isChecked })),
    );
  }

  toggleAllRates(event: Event) {
    const target = event.target as HTMLInputElement;
    const isChecked = target.checked;
    this.flatRates.update((rates) =>
      rates.map((r) => ({ ...r, checked: isChecked })),
    );
  }

  addWithAutoNo<T extends { no?: number; newFG?: boolean }>(
    list: T[],
    isMemberLevel: boolean,
    ctor: new () => T
  ) {
    const maxNo =
      list.length > 0
        ? Math.max(...list.map(x => x.no ?? 0))
        : 0;

    const item = new ctor();
    item.no = maxNo + 1;
    item.newFG = true;
    if (isMemberLevel) this.selectedLevelEdit = item;
    else this.selectedMemberPriceEdit = item;
    list.unshift(item);
  }

  oldmembeR_FG: string = '';
  oldsavE_CHARGE_FG: string = '';
  toggleRadiomembeR_FG(value: string) {
    if (this.oldmembeR_FG === value) {
      this.oldmembeR_FG = '';
      this.storeInfo.membeR_FG = '';
    }
    else {
      this.oldmembeR_FG = value;
      this.storeInfo.membeR_FG = value;
    }
  }

  toggleRadiosavE_CHARGE_FG(value: string) {
    if (this.oldsavE_CHARGE_FG === value) {
      this.oldsavE_CHARGE_FG = '';
      this.storeInfo.savE_CHARGE_FG = '';
    }
    else {
      this.oldsavE_CHARGE_FG = value;
      this.storeInfo.savE_CHARGE_FG = value;
    }
  }

  addMemberLevel() {
    if (this.levels.length >= this.memberTPList.length || this.levels.some(i => i.newFG == true)) return;
    this.addWithAutoNo(this.levels, true, M_MEMBER_LEVEL);
  }

  addFlatRate() {
    if (this.memberPrices.length >= this.memberLvlList.length || this.memberPrices.some(i => i.newFG == true)) return;
    this.addWithAutoNo(this.memberPrices, false, M_MEMBER_PRICE);
  }

  // --- HOURS Tab Methods ---

  firtsGetTabBusiness: boolean = true;
  businessHours = new Array<M_STORE_TIME>();

  handleSaveBusinessHours() {
    if (this.hoursTab() == "SETTINGS") this.saveBusinessHour();
  }

  saveBusinessHour() {
    const param = this.businessHours.map(x => x.toJSON());
    this.settingsService.timePost(param).subscribe((res: any) => {
      if (res?.isOK) {
        this.masterService.showSuccess(this.translate.instant('SYSMESER0006'))
        this.getWorkTime();
      }
      else {
        this.masterService.showError(Constants.showError(res?.errorMessages))
      }
    })
  }
  getDataBusinessHours() {
    if (!this.firtsGetTabBusiness) return;
    this.getWorkTime();
    this.getHoliday();
    this.firtsGetTabBusiness = false;
  }

  dayCommons = new Array();
  async getDayCommon() {
    const res: any = await this.masterService.getCommNoLoading('ST07');

    if (res?.isOK) {
      this.dayCommons = res?.result?.dataRes[0]?.rows;
      this.cdr.detectChanges();
    }
  }
  getWorkTime() {
    //SP_STR_GET_WORK_TIME
    this.masterService.excute_sp('6a83a168-298d-11f1-9014-00155d001e02', this.paramStaff).subscribe((res: any) => {
      if (res?.isOK) {
        const data = new M_STORE_TIME().getAll(res?.result?.dataRes[0]?.rows);
        data.forEach(x => {
          x.isUse = x.usE_YN === "Y";
        })
        this.businessHours = data.filter(x => x.timE_TP === "0");

        const breakTimes = data.filter(x => x.timE_TP === "1");
        this.businessHours.forEach(element => {
          element.breakTimes = breakTimes.filter(x => x.day === element.day);
        })

        const existingDays = new Set(
          this.businessHours.map(x => x.day)
        );

        const newItems = this.dayCommons
          .filter(dc => !existingDays.has(dc.comM_D_CD))
          .map(dc => {
            const item = new M_STORE_TIME();
            item.timE_TP = '0';
            item.day = dc.comM_D_CD;
            item.starT_TIME = new M_STORE_TIME().formatTime('')
            item.enD_TIME = new M_STORE_TIME().formatTime('')
            item.breakTimes = [];
            return item;
          });

        this.businessHours = [...this.businessHours, ...newItems];
        this.businessHours.sort((a, b) =>
          Number(a.day) - Number(b.day)
        );

      }
      this.cdr.detectChanges()
    })
  }

  holidayList = new Array<M_STORE_HOLIDAY>();
  holidays = new Array<M_STORE_HOLIDAY>();
  yearHolidays = new Array<M_STORE_HOLIDAY>();
  getHoliday() {
    //SP_STR_GET_HOLIDAY
    this.masterService.excute_sp('6a839b63-298d-11f1-9014-00155d001e02', this.paramStaff).subscribe((res: any) => {
      let data = new M_STORE_HOLIDAY().getAll(res?.result?.dataRes[0]?.rows)
      this.holidayList = data;
      this.yearHolidays = data.filter(x => x.holidaY_TP === '0' && x.year === this.yearSettingsDate().getFullYear().toString());
      this.yearHolidays.forEach(x => x.isUse = x.holidaY_YN === "Y")
      const existingDays = new Set(
        this.yearHolidays.map(x => x.day)
      );

      const newItems = this.dayCommons
        .filter(dc => !existingDays.has(dc.comM_D_CD))
        .map(dc => {
          const item = new M_STORE_HOLIDAY();
          item.holidaY_TP = '0';
          item.holidaY_SEQ = 0;
          item.year = this.yearSettingsDate().getFullYear().toString();
          item.day = dc.comM_D_CD;
          item.isUse = false;
          return item;
        });

      this.yearHolidays = [...this.yearHolidays, ...newItems];
      this.yearHolidays.sort((a, b) =>
        Number(a.day) - Number(b.day)
      );

      this.updateweeklyClosedDays();

      this.holidays = data.filter(x => x.holidaY_TP === '1');
      this.holidays.forEach(x => {
        x.isUse = x.holidaY_YN === "Y";
        const dateStr = `${x.year}-${x.month}-${x.date}`
        this.closedDates.update((dates) => {
          if (dates.includes(dateStr) && x.holidaY_YN === "Y") {
            return dates;
          }
          return [...dates, dateStr]
        });
      })
      this.cdr.detectChanges()
    })
  }

  insertOrUpdateHoliday(holiday: M_STORE_HOLIDAY, isYearHoliday: boolean = true) {
    const param = {
      HOLIDAY_SEQ: holiday.holidaY_SEQ,
      HOLIDAY_TP: holiday.holidaY_TP,
      DAY: holiday.day,
      USE_YN: !holiday.isUse ? 'Y' : 'N',
      Year: holiday.year,
      Month: holiday.month,
      Date: holiday.date,
      SETTING_DATE: this.currentDate()
    }

    this.settingsService.holidayPost(param).subscribe((res: any) => {
      if (res?.isOK) {
        this.masterService.showSuccess(this.translate.instant('SYSMESER0006'))
        this.getHoliday();
      }
      else {
        if (isYearHoliday) {
          const index = this.yearHolidays.findIndex(x => x.day === holiday.day);
          if (index !== -1) {
            this.yearHolidays[index] = {
              ...this.yearHolidays[index],
              isUse: !holiday.isUse
            } as M_STORE_HOLIDAY;
            this.cdr.detectChanges();
          }
        }
        else {
          const targetDate =
            holiday.year +
            '-' +
            holiday.month.toString().padStart(2, '0') +
            '-' +
            holiday.date.toString().padStart(2, '0');
          let holidayFail = this.holidays.find(x => x.year == holiday.year && x.month == holiday.month && x.date == holiday.date) || null;
          if (holidayFail) {
            holidayFail.isUse = !holiday.isUse;
          }
          this.closedDates.update(dates => {
            return dates.filter(d => d !== targetDate);
          });
        }
        this.masterService.showError(Constants.showError(res?.errorMessages))
      }
    })
  }

  addBreakTime(day: string) {
    this.businessHours = this.businessHours.map(d => {

      if (d.day !== day) return d;

      const updated = new M_STORE_TIME(d);
      const breakTimes = updated.breakTimes ?? [];

      const maxSeq = breakTimes.length
        ? Math.max(...breakTimes.map(x => x.timE_SEQ || 0))
        : 0;

      const newTime = new M_STORE_TIME();
      newTime.timE_TP = '1';
      newTime.timE_SEQ = maxSeq + 1;
      newTime.starT_TIME = '';
      newTime.enD_TIME = '';
      updated.breakTimes.push(newTime)

      return updated;
    });
  }
  removeBreakTime(day: string, seq: number) {

    this.businessHours = this.businessHours.map(d => {

      if (d.day !== day) return d;

      const updated = new M_STORE_TIME(d);

      updated.breakTimes = d.breakTimes.filter(x =>
        x.timE_SEQ !== seq
      );

      return updated;
    });
  }
  navigateMonth(direction: "prev" | "next") {
    this.currentDate.update((d) => {
      const newDate = new Date(d);
      newDate.setMonth(d.getMonth() + (direction === "next" ? 1 : -1));
      return newDate;
    });
    this.updateweeklyClosedDays();
  }

  updateweeklyClosedDays() {
    const yearHolidays = this.holidayList.filter(x => x.holidaY_TP === '0' && x.year === this.currentDate().getFullYear().toString());
    yearHolidays.forEach(x => x.isUse = x.holidaY_YN === "Y")
    this.weeklyClosedDays.set([]);
    yearHolidays.forEach(x => {
      if (x.isUse) {
        this.weeklyClosedDays.update((day: any) => {
          return day.includes(x.day)
            ? day.filter((d: any) => d !== x.day)
            : [...day, x.day];
        })
      }
    })
  }

  updateYearHoliday() {
    this.yearHolidays = this.holidayList.filter(x => x.holidaY_TP === '0' && x.year === this.yearSettingsDate().getFullYear().toString());
    const existingDays = new Set(
      this.yearHolidays.map(x => x.day)
    );

    this.yearHolidays.forEach(x => x.isUse = x.holidaY_YN === "Y")

    const newItems = this.dayCommons
      .filter(dc => !existingDays.has(dc.comM_D_CD))
      .map(dc => {
        const item = new M_STORE_HOLIDAY();
        item.holidaY_TP = '0';
        item.holidaY_SEQ = 0;
        item.year = this.yearSettingsDate().getFullYear().toString();
        item.day = dc.comM_D_CD;
        item.isUse = false;
        return item;
      });

    this.yearHolidays = [...this.yearHolidays, ...newItems];
    this.yearHolidays.sort((a, b) =>
      Number(a.day) - Number(b.day)
    );

  }

  navigateYearPicker(data: any) {
    this.yearSettingsDate.update((d) => {
      var newDate = new Date(data)
      return newDate;
    });
    this.updateYearHoliday();
    this.updateweeklyClosedDays();
  }
  navigateYear(direction: "prev" | "next") {
    this.yearSettingsDate.update((d) => {
      const newDate = new Date(d);
      newDate.setFullYear(d.getFullYear() + (direction === "next" ? 1 : -1));
      return newDate;
    });

    this.yearSettingsDatePicker = this.yearSettingsDate();
    this.updateYearHoliday();
  }
  toggleDate(date: Date) {
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const monthStr = month.toString().padStart(2, '0');
    const dayStr = day.toString().padStart(2, '0');
    let holiday = this.holidays.find(x => x.year === year.toString() && x.month === monthStr && x.date === dayStr);
    if (holiday) {
      holiday.isUse = !holiday.isUse;
      holiday.holidaY_YN = holiday.isUse ? 'Y' : 'N';
    }
    else {
      const yearHoliday = this.holidayList.find(x => x.year === year.toString() && x.day === (date.getDay() + 1).toString() && x.holidaY_TP == "0" && x.holidaY_YN == "Y");
      // console.log(yearHoliday)
      holiday = new M_STORE_HOLIDAY();
      holiday.year = year.toString();
      holiday.month = monthStr;
      holiday.date = dayStr;
      holiday.day = (date.getDay() + 1).toString();
      holiday.isUse = yearHoliday ? false : true;
      holiday.holidaY_YN = holiday.isUse ? 'Y' : 'N';
      holiday.holidaY_TP = "1";
    }
    this.insertOrUpdateHoliday(holiday, false)

    const dateStr = this.formatDate(date);
    this.closedDates.update((dates) => {
      return dates.includes(dateStr)
        ? dates.filter((d) => d !== dateStr)
        : [...dates, dateStr];
    });
  }
  toggleWeeklyDay(dayIndex: number, day: string) {
    // this.weeklyClosedDays.update((days) => {
    //   return days.includes(day)
    //     ? days.filter((d) => d !== day)
    //     : [...days, day];
    // });

    const yearHoliday = this.yearHolidays.find(x => x.day === day) || null;
    if (yearHoliday) {
      yearHoliday.isUse = !yearHoliday?.isUse;
      this.insertOrUpdateHoliday(yearHoliday);
    }

  }

  calendarDays = computed<CalendarDay[][]>(() => {
    const date = this.currentDate();
    const year = date.getFullYear();
    const month = date.getMonth();

    const firstDayOfMonth = new Date(year, month, 1);

    const firstDayOfWeek = firstDayOfMonth.getDay();

    const days: CalendarDay[] = [];

    const startDate = new Date(firstDayOfMonth);
    startDate.setDate(startDate.getDate() - firstDayOfWeek);

    const totalDaysInGrid = 35;

    for (let i = 0; i < totalDaysInGrid; i++) {
      const d = new Date(startDate);
      d.setDate(startDate.getDate() + i);
      days.push(this.createCalendarDay(d, d.getMonth() === month));
    }

    const weeks: CalendarDay[][] = [];
    for (let i = 0; i < days.length; i += 7) {
      weeks.push(days.slice(i, i + 7));
    }
    return weeks;
  });

  private createCalendarDay(date: Date, isCurrentMonth: boolean): CalendarDay {
    const dateStr = this.formatDate(date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const monthStr = month.toString().padStart(2, '0');
    const dayStr = day.toString().padStart(2, '0');
    let holiday = this.holidays.find(x => x.year === year.toString() && x.month === monthStr && x.date === dayStr);

    return {
      day: date.getDate(),
      date: date,
      isCurrentMonth: isCurrentMonth,
      isClosed: this.closedDates().includes(dateStr),
      isWeeklyClosed: this.weeklyClosedDays().includes((date.getDay() + 1).toString()),
      isToday: date.getTime() === today.getTime(),
      isHolidayYN: holiday?.holidaY_YN || ""
    };
  }

  private formatDate(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  }

  trackByWeek(index: number, week: CalendarDay[]): number {
    return index;
  }

  trackByDay(index: number, day: CalendarDay): number {
    return day.date.getTime();
  }

  protected readonly DAYS_ORDER = DAYS_ORDER;
  protected readonly formatDateToYYYYMMDD = formatDateToYYYYMMDD;
  protected readonly formatDateFromYYYYMMDD = formatDateFromYYYYMMDD;
}
